import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
  InputAdornment,
  Divider,
  Alert,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip
} from '@mui/material';

const WhiteLabelCostForm = ({ costs, partnerQuotes, selectedPartnerQuote, onChange }) => {
  const data = costs?.whiteLabel || {};
  const blank = data.blank || {};
  const decoration = data.decoration || {};
  const setupFees = data.setupFees || {};
  const additional = data.additional || {};

  const [costSource, setCostSource] = useState('manual');

  // Debug logging (removed to prevent infinite loop)
  // useEffect(() => {
  //   console.log('🎨 WhiteLabelCostForm received costs:', { costs, blank });
  // }, [costs, blank]);

  // Check if we have a selected partner quote
  useEffect(() => {
    if (selectedPartnerQuote && partnerQuotes && partnerQuotes.length > 0) {
      const quote = partnerQuotes.find(q => q.partnerId === selectedPartnerQuote.partnerId);
      if (quote) {
        setCostSource('quote');
      }
    }
  }, [selectedPartnerQuote, partnerQuotes]);

  // Auto-calculate total unit cost
  useEffect(() => {
    if (costSource === 'manual') {
      const blankCost = parseFloat(blank.cost) || 0;
      const printCost = parseFloat(decoration.print) || 0;
      const embroidCost = parseFloat(decoration.embroidery) || 0;
      const heatTransferCost = parseFloat(decoration.heatTransfer) || 0;
      const otherDecorCost = parseFloat(decoration.other) || 0;
      const setupPerUnit = parseFloat(setupFees.perUnitCost) || 0;
      const labelsCost = parseFloat(additional.labels) || 0;
      const hangtagsCost = parseFloat(additional.hangtags) || 0;
      const packagingCost = parseFloat(additional.packaging) || 0;

      const total = blankCost + printCost + embroidCost + heatTransferCost +
                    otherDecorCost + setupPerUnit + labelsCost + hangtagsCost +
                    packagingCost;

      const roundedTotal = total > 0 ? parseFloat(total.toFixed(2)) : undefined;

      // Only update if the total has changed (prevent infinite loops)
      if (roundedTotal !== data.totalUnitCost) {
        onChange({
          ...costs,
          whiteLabel: {
            ...data,
            totalUnitCost: roundedTotal
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on the actual values, not the objects
    blank.cost,
    decoration.print,
    decoration.embroidery,
    decoration.heatTransfer,
    decoration.other,
    setupFees.perUnitCost,
    additional.labels,
    additional.hangtags,
    additional.packaging,
    costSource,
    data.totalUnitCost
    // Intentionally not including: costs, onChange, data (objects cause re-renders)
  ]);

  // Calculate setup fee per unit when quantity or setup fees change
  useEffect(() => {
    // This would be calculated based on planned units from the drop config
    // For now, just allow manual entry
  }, [setupFees.screens, setupFees.digitization]);

  const handleChange = (category, field) => (event) => {
    const value = event.target.value;
    const numValue = value === '' ? undefined : parseFloat(value);

    if (category) {
      onChange({
        ...costs,
        whiteLabel: {
          ...data,
          [category]: {
            ...(data[category] || {}),
            [field]: numValue
          }
        }
      });
    } else {
      onChange({
        ...costs,
        whiteLabel: {
          ...data,
          [field]: numValue
        }
      });
    }
  };

  const handleStringChange = (category, field) => (event) => {
    const value = event.target.value;

    onChange({
      ...costs,
      whiteLabel: {
        ...data,
        [category]: {
          ...(data[category] || {}),
          [field]: value || undefined
        }
      }
    });
  };

  // Get selected partner quote details
  const getSelectedQuote = () => {
    if (!selectedPartnerQuote || !partnerQuotes || partnerQuotes.length === 0) {
      return null;
    }
    return partnerQuotes.find(q => q.partnerId === selectedPartnerQuote.partnerId);
  };

  const selectedQuote = getSelectedQuote();

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          White Label Production Costs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Decorating pre-made blanks - enter per-unit costs
        </Typography>

        {/* Cost Source Selection */}
        {partnerQuotes && partnerQuotes.length > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Cost Source
            </Typography>
            <RadioGroup
              row
              value={costSource}
              onChange={(e) => setCostSource(e.target.value)}
            >
              <FormControlLabel
                value="quote"
                control={<Radio />}
                label="Partner Quote"
                disabled={!selectedQuote}
              />
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label="Manual Entry"
              />
            </RadioGroup>

            {costSource === 'quote' && selectedQuote && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Using quote from <strong>{selectedQuote.partnerName}</strong>
                {' '}(${selectedQuote.totalCost.toFixed(2)} per unit)
                <br />
                <Typography variant="caption">
                  Quoted: {new Date(selectedQuote.quotedAt).toLocaleDateString()}
                  {' '}| Expires: {new Date(selectedQuote.expiresAt).toLocaleDateString()}
                </Typography>
              </Alert>
            )}

            {costSource === 'quote' && !selectedQuote && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No partner quote selected. Switch to Manual Entry to input costs.
              </Alert>
            )}
          </Box>
        )}

        {/* Show quote breakdown if using partner quote */}
        {costSource === 'quote' && selectedQuote ? (
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Quote Breakdown
                </Typography>
                <Stack spacing={1}>
                  {selectedQuote.breakdown?.baseCost > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Base/Blank Cost</Typography>
                      <Typography variant="body2" fontWeight={600}>${selectedQuote.breakdown.baseCost.toFixed(2)}</Typography>
                    </Box>
                  )}
                  {selectedQuote.breakdown?.decorationCost > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Decoration Cost</Typography>
                      <Typography variant="body2" fontWeight={600}>${selectedQuote.breakdown.decorationCost.toFixed(2)}</Typography>
                    </Box>
                  )}
                  {selectedQuote.breakdown?.setupFee > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Setup Fee (per unit)</Typography>
                      <Typography variant="body2" fontWeight={600}>${selectedQuote.breakdown.setupFee.toFixed(2)}</Typography>
                    </Box>
                  )}
                  {selectedQuote.breakdown?.shipping > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Shipping (per unit)</Typography>
                      <Typography variant="body2" fontWeight={600}>${selectedQuote.breakdown.shipping.toFixed(2)}</Typography>
                    </Box>
                  )}
                  {selectedQuote.breakdown?.fees > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Other Fees</Typography>
                      <Typography variant="body2" fontWeight={600}>${selectedQuote.breakdown.fees.toFixed(2)}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight={700}>Total Per Unit</Typography>
                    <Typography variant="body1" fontWeight={700} color="primary">
                      ${selectedQuote.totalCost.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <TextField
              label="Suggested Retail Price"
              type="number"
              size="small"
              value={data.suggestedRetail ?? ''}
              onChange={handleChange(null, 'suggestedRetail')}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Your target retail price"
              sx={{ maxWidth: 300 }}
            />

            {selectedQuote.totalCost > 0 && data.suggestedRetail > 0 && (
              <Alert severity="info">
                Markup: {((data.suggestedRetail / selectedQuote.totalCost - 1) * 100).toFixed(1)}%
                {' '}| Margin: {((data.suggestedRetail - selectedQuote.totalCost) / data.suggestedRetail * 100).toFixed(1)}%
              </Alert>
            )}
          </Stack>
        ) : (
          /* Manual Entry Form */
          <Stack spacing={3}>
            {/* Base Garment */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Base Garment
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Blank Cost"
                    type="number"
                    size="small"
                    value={blank.cost ?? ''}
                    onChange={handleChange('blank', 'cost')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Cost from supplier"
                    fullWidth
                  />
                  <TextField
                    label="Supplier Name"
                    size="small"
                    value={blank.supplierName ?? ''}
                    onChange={handleStringChange('blank', 'supplierName')}
                    helperText="e.g., SSActivewear, Apliiq"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Catalog Item ID / SKU"
                  size="small"
                  value={blank.catalogItemId ?? ''}
                  onChange={handleStringChange('blank', 'catalogItemId')}
                  helperText="Supplier's item code"
                  sx={{ maxWidth: 400 }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Decoration */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Decoration
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Screen Print"
                    type="number"
                    size="small"
                    value={decoration.print ?? ''}
                    onChange={handleChange('decoration', 'print')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                  <TextField
                    label="Embroidery"
                    type="number"
                    size="small"
                    value={decoration.embroidery ?? ''}
                    onChange={handleChange('decoration', 'embroidery')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Heat Transfer / DTG"
                    type="number"
                    size="small"
                    value={decoration.heatTransfer ?? ''}
                    onChange={handleChange('decoration', 'heatTransfer')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                  <TextField
                    label="Other Decoration"
                    type="number"
                    size="small"
                    value={decoration.other ?? ''}
                    onChange={handleChange('decoration', 'other')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    fullWidth
                  />
                </Stack>
              </Stack>
            </Box>

            <Divider />

            {/* Setup & Fees */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Setup & Fees
              </Typography>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Screen Setup (total)"
                    type="number"
                    size="small"
                    value={setupFees.screens ?? ''}
                    onChange={handleChange('setupFees', 'screens')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="One-time setup cost"
                    fullWidth
                  />
                  <TextField
                    label="Digitization (total)"
                    type="number"
                    size="small"
                    value={setupFees.digitization ?? ''}
                    onChange={handleChange('setupFees', 'digitization')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Embroidery file prep"
                    fullWidth
                  />
                </Stack>
                <TextField
                  label="Setup Cost Per Unit"
                  type="number"
                  size="small"
                  value={setupFees.perUnitCost ?? ''}
                  onChange={handleChange('setupFees', 'perUnitCost')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  helperText="Setup fees divided by quantity"
                  sx={{ maxWidth: 300 }}
                />
              </Stack>
            </Box>

            <Divider />

            {/* Additional Services */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Additional Services
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Custom Labels/Tags"
                  type="number"
                  size="small"
                  value={additional.labels ?? ''}
                  onChange={handleChange('additional', 'labels')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  fullWidth
                />
                <TextField
                  label="Hangtags"
                  type="number"
                  size="small"
                  value={additional.hangtags ?? ''}
                  onChange={handleChange('additional', 'hangtags')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  fullWidth
                />
                <TextField
                  label="Custom Packaging"
                  type="number"
                  size="small"
                  value={additional.packaging ?? ''}
                  onChange={handleChange('additional', 'packaging')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            {/* Totals */}
            <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Total Unit Cost"
                  type="number"
                  size="small"
                  value={data.totalUnitCost ?? ''}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    readOnly: true
                  }}
                  helperText="Auto-calculated from fields above"
                  sx={{
                    bgcolor: 'background.paper',
                    '& .MuiInputBase-input': { fontWeight: 600 }
                  }}
                  fullWidth
                />
                <TextField
                  label="Suggested Retail Price"
                  type="number"
                  size="small"
                  value={data.suggestedRetail ?? ''}
                  onChange={handleChange(null, 'suggestedRetail')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  helperText="Your target retail price"
                  fullWidth
                />
              </Stack>
            </Box>

            {data.totalUnitCost > 0 && data.suggestedRetail > 0 && (
              <Alert severity="info">
                Markup: {((data.suggestedRetail / data.totalUnitCost - 1) * 100).toFixed(1)}%
                {' '}| Margin: {((data.suggestedRetail - data.totalUnitCost) / data.suggestedRetail * 100).toFixed(1)}%
              </Alert>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default WhiteLabelCostForm;
